import React, {useMemo} from 'react'
import {selectCartItems} from '../../store/slices/shoppingCartSlice'
import {selectCartValidationErrors} from '../../store/slices/shoppingCartValidationSlice'
import {useAppSelector} from '../../store/hooks'
import * as types from '../../schema'
import ShoppingCartItem from './ShoppingCartItem'
import clsx from 'clsx'
import {formatCurrency} from '../../services/utils'
import {getLogger} from '../../services/logging'
import EmptyCart from '../../assets/ibe-empty-cart.png'
import {calcCartPriceSummary} from '../../services/offerUtils'

const logger = getLogger('ShoppingCart')

export interface ShoppingCartProps {
  className?: string
  validationMode?: boolean
}

const ShoppingCart = ({
  className = '',
  validationMode = false
}: ShoppingCartProps) => {
  const cartItems = useAppSelector(selectCartItems)
  const cartValidationErrors = useAppSelector(selectCartValidationErrors)
  const validationErrorLookup = cartValidationErrors.reduce(
    (acc: {[key: string]: types.ShoppingCartValidationError}, error) => {
      acc[error.id] = error
      return acc
    },
    {}
  )
  const cartTotals = useMemo(() => {
    return calcCartPriceSummary(cartItems)
  }, [cartItems])
  const hasPrepayment = cartTotals.totalPrepayment > 0

  logger.debug({
    message: 'Rendering ShoppingCart...',
    data: {cartItems, cartTotals}
  })

  return (
    <div className={clsx('flex flex-col', className)}>
      {!cartItems || cartItems.length === 0 ? (
        <div className="my-8 flex flex-col gap-4 items-center">
          <img
            src={EmptyCart}
            alt="Shopping cart is empty image"
            className="max-h-72"
          />
          <h2 className="text-xl font-semibold">
            Your shopping cart is empty.
          </h2>
        </div>
      ) : (
        <>
          <div className="card card-compact border bg-white shadow w-full">
            <div className="card-body flex flex-col items-end gap-2 w-full">
              {/* Shopping Cart Items */}
              <div className="flex flex-col gap-2 w-full">
                {cartItems.map(
                  (item: types.ShoppingCartItem, index: number) => {
                    const validationError = validationMode
                      ? validationErrorLookup[item.id]
                      : null
                    return (
                      <div key={item.id} className="w-full">
                        <ShoppingCartItem
                          item={item}
                          itemIndex={index}
                          isCart={!validationMode}
                          showItemIndex={cartItems?.length > 1}
                          validationError={validationError}
                        />

                        <div className="border-t border-1 border-accent my-8"></div>
                      </div>
                    )
                  }
                )}
              </div>

              {/* Shopping Cart Totals */}
              <div className="w-full flex flex-col items-end bg-gray-100 rounded-xl p-4">
                <div className="flex flex-row gap-1 text-right">
                  <span className="text-md font-semibold">Total:</span>
                  <span className="text-md font-bold w-20">
                    {formatCurrency(cartTotals.total)}
                  </span>
                </div>

                {hasPrepayment && (
                  <div className="flex flex-row gap-1 text-right">
                    <div className="flex flex-col">
                      <span className="text-md font-semibold">
                        * Due at Checkout:
                      </span>
                    </div>
                    <span className="text-md font-bold w-20">
                      {formatCurrency(cartTotals.totalPrepayment)}
                    </span>
                  </div>
                )}

                {hasPrepayment && (
                  <div className="text-center italic w-full text-xs text-gray-600 mt-2">
                    * All non-refundable and monthly room rates require full or
                    partial prepayment at time of booking.
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default ShoppingCart
